import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { Card, CardBody, Col, Container, Button, Spinner } from "reactstrap";
import PublicPageContext from "../../context/PublicPageContext/PublicPageContext";
import { useSearchParams } from "react-router-dom";
import { CheckCircle } from "react-feather";
import { fetchPublicEventDetail } from "../../api/Events/EventsApi";

// Helper: convert basic markdown formatting to HTML for label fields
const formatLabelText = (text) => {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Replace URLs with clickable <a> links, excluding trailing punctuation and newlines
  html = html.replace(
    /(https?:\/\/[^\s<\\]+[^.,:;"'()!?\s<\\])/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; font-weight: bold; color: #7366ff;">$1</a>'
  );

  // Parse parenthesized markdown link brackets e.g. [Label](<a href="URL"...>URL</a>)
  html = html.replace(
    /\[([^\]]+)\]\(<a href="([^"]+)"[^>]*>.*?<\/a>\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; font-weight: bold; color: #7366ff;">$1</a>'
  );

  html = html
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\\n|\n/g, '<br/>');   // handles both literal \n text and actual newline (Enter)
  return html;
};

const ThankyouPage = () => {
  const { bankId, trustUuid, orgId, templeUuid, accontId } = useParams();
  const [searchParams] = useSearchParams();

  const type = searchParams.get("type");
  const eventUuid = searchParams.get("eventUuid");
  const history = useNavigate();
  const { publicPageThankYou } = useContext(PublicPageContext);

  const [eventDetail, setEventDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (type === "event" && eventUuid) {
      const loadEventDetails = async () => {
        setLoading(true);
        try {
          const { data } = await fetchPublicEventDetail(eventUuid);
          if (data && data.status === 200) {
            const detail = data.payload?.data || data.payload;
            if (detail && detail.formFields) {
              if (typeof detail.formFields === 'string') {
                try {
                  detail.formFields = JSON.parse(detail.formFields);
                } catch (e) {
                  console.error("Error parsing formFields:", e);
                  detail.formFields = [];
                }
              }
              if (!Array.isArray(detail.formFields)) {
                try {
                  detail.formFields = Object.values(detail.formFields);
                } catch (e) {
                  detail.formFields = [];
                }
              }
            }
            setEventDetail(detail);
          }
        } catch (error) {
          console.error("Failed to load event detail:", error);
        } finally {
          setLoading(false);
        }
      };
      loadEventDetails();
    }
  }, [type, eventUuid]);

  const redirect = () => {
    if (type === 'event' && eventUuid) {
      history(`/EventRegister?eventUuid=${eventUuid}`);
    } else if (type === 'due_multi') {
      history(`/mahotsav-donation/${orgId}/${templeUuid}/${trustUuid}/${bankId}`);
    } else if (accontId) {
      history(`/mahotsav-donation/${orgId}/${templeUuid}/${trustUuid}/${bankId}/${accontId}`);
    } else if (publicPageThankYou === true) {
      history(`/donation/${orgId}/${templeUuid}/${trustUuid}/${bankId}`);
    } else {
      history(`/DonarPage/${orgId}/${templeUuid}`);
    }
  };

  const getSuccessMessage = () => {
    if (type === 'event') {
      return "Event Registration Successful! 🎉";
    }
    if (type === 'due_multi') {
      return "Donation Accepted ✅";
    }
    if (publicPageThankYou === true && !accontId) {
      return "Donation Accepted ✅";
    }
    if (!accontId && publicPageThankYou === false) {
      return "Donar Created ✅";
    }
    if (accontId) {
      return "Mahotsav Donation Accepted ✅";
    }
    return "Action Completed Successfully ✅";
  };

  return (
    <div 
      style={{ 
        background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)", 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        padding: "1.5rem" 
      }}
    >
      <Container>
        <div className="row justify-content-center">
          <Col xl="5" lg="6" md="8" sm="10" xs="12">
            <Card className="shadow-lg border-0" style={{ borderRadius: "20px", overflow: "hidden" }}>
              <div 
                style={{ 
                  background: "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)", 
                  padding: "2.5rem 2rem", 
                  textAlign: "center", 
                  color: "#fff" 
                }}
              >
                <div className="mb-3 d-inline-flex align-items-center justify-content-center" style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", borderRadius: "50%", padding: "1rem" }}>
                  <CheckCircle size={48} className="text-white" />
                </div>
                <h2 className="font-weight-bold mb-2" style={{ fontSize: "1.85rem", letterSpacing: "-0.5px" }}>
                  Thank You!
                </h2>
                <p className="mb-0 opacity-90" style={{ fontSize: "1rem" }}>
                  {type === 'event' ? "Your details have been recorded." : "We appreciate your support."}
                </p>
              </div>

              <CardBody className="p-4 p-sm-5 text-center" style={{ backgroundColor: "#ffffff" }}>
                <h4 className="text-success font-weight-bold mb-4" style={{ fontSize: "1.25rem", lineHeight: "1.5" }}>
                  {getSuccessMessage()}
                </h4>

                {loading && (
                  <div className="text-center my-3">
                    <Spinner size="sm" color="primary" />
                  </div>
                )}

                {type === 'event' && eventDetail && eventDetail.formFields && (
                  <div className="mt-2 mb-4">
                    {eventDetail.formFields
                      .filter(field => field.type === 'label')
                      .map(field => {
                        const links = [];
                        let cleanText = field.label;

                        // 1. Check for markdown links: [Button Text](URL)
                        const markdownMatches = [...field.label.matchAll(/\[([^\]]+)\]\((https?:\/\/[^\s<\\]+[^.,:;"'()!?\s<\\])\)/g)];
                        markdownMatches.forEach(match => {
                          links.push({
                            url: match[2],
                            text: match[1]
                          });
                          cleanText = cleanText.replace(match[0], '');
                        });

                        // 2. Fallback to plain URLs if no markdown links found
                        if (links.length === 0) {
                          const plainUrls = field.label.match(/(https?:\/\/[^\s<\\]+[^.,:;"'()!?\s<\\])/g) || [];
                          plainUrls.forEach(url => {
                            links.push({
                              url: url,
                              text: "Join Link"
                            });
                            cleanText = cleanText.replace(url, '');
                          });
                        }

                        if (links.length === 0) return null;

                        // Clean up emoji and trim
                        cleanText = cleanText.replace(/🔗/g, '').trim();

                        return (
                          <div 
                            key={field.fieldKey} 
                            className="alert alert-info p-3 mb-3 text-center" 
                            style={{ 
                              borderRadius: "12px", 
                              borderLeft: "4px solid #0dcaf0", 
                              backgroundColor: "#f0fafd", 
                              color: "#055160",
                              textAlign: "left"
                            }}
                          >
                            {cleanText && (
                              <p className="mb-3" style={{ fontSize: "0.95rem", lineHeight: "1.5" }} dangerouslySetInnerHTML={{ __html: formatLabelText(cleanText) }} />
                            )}
                            <div className="d-flex flex-column gap-2">
                              {urls => null /* unused but mapped cleanly */}
                              {links.map((link, idx) => {
                                return (
                                  <Button
                                    key={idx}
                                    color="success"
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-100 font-weight-bold d-flex align-items-center justify-content-center"
                                    style={{ 
                                      borderRadius: "8px", 
                                      padding: "0.6rem",
                                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                      border: "none",
                                      boxShadow: "0 4px 6px rgba(16, 185, 129, 0.15)"
                                    }}
                                  >
                                    {link.text}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}

                {type === 'event' && (
                  <p className="text-muted mb-5" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                    Your registration for the event has been successfully completed. If you want to register another family member or participant, click the button below.
                  </p>
                )}

                <Button
                  onClick={redirect}
                  className="w-100"
                  style={{
                    padding: "0.85rem",
                    borderRadius: "10px",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    background: "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
                    border: "none",
                    boxShadow: "0 4px 10px rgba(79, 70, 229, 0.25)"
                  }}
                >
                  {type === 'event' ? "Register Another Person" : "Back to Home"}
                </Button>
              </CardBody>
            </Card>
          </Col>
        </div>
      </Container>
    </div>
  );
};

export default ThankyouPage;
